<!--
InputWriteOnlySecret Component — a write-only secret field with a stored state.

Use for any secret the backend never echoes back (only a boolean "has one on
file" flag), where a save must not read "no value in this submit" as "delete
the stored secret" (a partial PUT is not a DELETE). Today's only consumer is
the Focus NFe token (services/app-frontend-service/src/domains/subaccounts/
fiscal-config/FiscalConfigForm.vue) — this is intentionally a single-use
extraction, not a speculative abstraction: the invariant below is the reason
it exists as its own component.

THE INVARIANT (do not weaken): `modelValue` must NEVER be hydrated from an
API response. This is the exact inverse of the bug that lost the Chile SII
password (task #64) — the parent form is trusted to never write into this
component's v-model from a GET, and this component itself never fills its
own field from `hasStoredSecret` becoming true.

States:
  A. hasStoredSecret=true,  not replacing  -> status tag + "replace" button,
     NO editable field in the DOM.
  B. hasStoredSecret=true,  replacing      -> empty password field + "cancel".
  C. hasStoredSecret=false                 -> password field directly.

Transitions:
  A -> B (replace clicked): emits update:modelValue('') BEFORE revealing the
       field — matches the parent's "revealed field is always empty" premise.
  B -> A (cancel clicked): emits update:modelValue('') and hides the field —
       clearing the v-model is part of the transition, not a side effect.
  hasStoredSecret prop changes (e.g. a fresh GET after save): replacing is
       reset to false and update:modelValue('') is emitted.
-->
<template>
  <div class="input-write-only-secret">
    <label v-if="label" :for="elemId" class="input-label">{{ label }}</label>

    <div v-if="hasStoredSecret && !replacing" class="secret-status-row">
      <status-tag severity="success" :data-testid="`${elemId}-status`">{{ statusLabel }}</status-tag>
      <Button
        :data-testid="`${elemId}-replace-btn`"
        :label="replaceLabel"
        outlined
        size="small"
        @click="startReplace" />
    </div>

    <div v-else class="secret-input-row">
      <input-password
        :elem-id="elemId"
        :data-testid="`${elemId}-input`"
        :placeholder="placeholder"
        :model-value="modelValue"
        @update:modelValue="onInput" />
      <Button
        v-if="hasStoredSecret"
        :data-testid="`${elemId}-cancel-btn`"
        :label="cancelLabel"
        text
        size="small"
        @click="cancel" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

interface Props {
  elemId: string;
  label: string;
  hasStoredSecret: boolean;
  modelValue: string;
  statusLabel: string;
  replaceLabel: string;
  cancelLabel: string;
  placeholder?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const replacing = ref<boolean>(false);

function startReplace(): void {
  emit('update:modelValue', '');
  replacing.value = true;
}

function cancel(): void {
  replacing.value = false;
  emit('update:modelValue', '');
}

function onInput(value: string): void {
  emit('update:modelValue', value);
}

watch(
  () => props.hasStoredSecret,
  () => {
    replacing.value = false;
    emit('update:modelValue', '');
  }
);
</script>

<style scoped>
.input-write-only-secret {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
  color: var(--p-text-color);
}

.secret-status-row,
.secret-input-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.secret-input-row :deep(.p-password) {
  flex: 1;
}
</style>
